import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { IShopSchedule } from '@core/models/shop.model';
import { TranslateService } from '@ngx-translate/core';
import { DateUtils } from '../../utils/date/date.utils';
import { TagAtomComponent } from '../tag-atom/tag-atom.component';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  createCalendarOfTheNext90Days,
  IDisplaySchedule,
} from './create-calendar-of-the-next-90-days';

@Component({
  selector: 'app-shop-schedules-atom',
  templateUrl: './shop-schedules-atom.component.html',
  styleUrls: ['./shop-schedules-atom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('isAccordionActive', [
      state(
        'hidden',
        style({
          height: '0',
          overflow: 'hidden',
        }),
      ),
      state(
        'visible',
        style({
          height: '*',
        }),
      ),
      transition('visible <=> hidden', [
        style({ overflow: 'hidden' }),
        animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'),
      ]),
      transition('void => *', animate(0)),
    ]),
  ],
  standalone: false,
})
export class ShopSchedulesAtomComponent {
  readonly schedules = input.required<IShopSchedule[]>();
  readonly isShopMoveable = input<boolean>(false);

  protected readonly isAccordionActive = signal(false);
  protected readonly isCalendarPerturbationAccordionActive = signal(false);

  private readonly calendarOfThe90NextDays = computed(() => {
    const schedules = this.schedules();

    return createCalendarOfTheNext90Days({
      schedules,
      scheduleStatusColorMap: this.scheduleStatusColorMap,
    });
  });

  protected readonly groupedSchedules = computed(() => {
    return this.calendarOfThe90NextDays().slice(0, 7);
  });

  protected readonly calendarPerturbationsAfterToday = computed(() => {
    const calendarOfThe90NextDays = this.calendarOfThe90NextDays();
    const calendarPerturbationsAfterToday = calendarOfThe90NextDays
      .slice(1)
      .filter(calendarDay => {
        return (
          calendarDay.status === 'exceptionallyClosed' || calendarDay.status === 'reducedHours'
        );
      })
      .map(calendarDay => {
        return {
          ...calendarDay,
          dateText: dayjs(calendarDay.date).format('dddd D MMMM'),
        };
      });
    return calendarPerturbationsAfterToday;
  });

  protected readonly firstPerturbationsAfterToday = computed(() => {
    return this.calendarPerturbationsAfterToday()[0];
  });

  private readonly scheduleStatusColorMap: Record<
    IDisplaySchedule['status'],
    TagAtomComponent['color']
  > = {
    reducedHours: 'highlight',
    closed: 'neutral',
    exceptionallyClosed: 'error',
    open: 'success',
  };

  constructor(private readonly translateService: TranslateService) {
    dayjs.extend(relativeTime);
  }

  private readonly headerTranslations = toSignal(
    this.translateService.get([
      'shop-informations.schedules.open',
      'shop-informations.schedules.at',
      'shop-informations.schedules.open-until',
      'shop-informations.schedules.open-today-at',
      'shop-informations.schedules.format',
    ]),
  );

  protected readonly scheduleHeader = computed(() => {
    const translations = this.headerTranslations();
    if (!translations) {
      return '';
    }
    const minutesSinceStartOfDay = DateUtils.dayjsInMarketplaceTimezone().diff(
      DateUtils.dayjsInMarketplaceTimezone().startOf('day'),
      'minute',
    );
    const currentDayOfWeek = DateUtils.dayjsInMarketplaceTimezone().day();
    const schedulesOfTheDay = this.groupedSchedules().find(
      schedule => schedule.day.index === currentDayOfWeek,
    );

    if (!schedulesOfTheDay) {
      return '';
    }

    const schedule = schedulesOfTheDay.timeSlots.find(
      slot => slot.start <= minutesSinceStartOfDay && slot.end >= minutesSinceStartOfDay,
    );

    if (schedule) {
      return `${translations['shop-informations.schedules.open-until']} ${DateUtils.dayjsInMarketplaceTimezone().startOf('day').add(schedule.end!, 'minute').format(translations['shop-informations.schedules.format'])}`;
    }
    const nextOpenScheduleToday = schedulesOfTheDay.timeSlots.find(
      s => s.end > minutesSinceStartOfDay && s.start >= minutesSinceStartOfDay && !s.isClosed,
    );
    if (nextOpenScheduleToday) {
      return `${translations['shop-informations.schedules.open-today-at']} ${DateUtils.dayjsInMarketplaceTimezone().startOf('day').add(nextOpenScheduleToday.start, 'minute').format(translations['shop-informations.schedules.format'])}`;
    }

    const schedulesWithOpenScheduleOfTheWeek = this.findNextOpenSchedule(currentDayOfWeek);

    if (!schedulesWithOpenScheduleOfTheWeek) {
      return '';
    }
    const nextOpenedScheduleOfTheWeek = schedulesWithOpenScheduleOfTheWeek.timeSlots.find(
      s => s.isClosed === false,
    );
    if (!nextOpenedScheduleOfTheWeek) {
      return '';
    }
    const isNextWeek = schedulesWithOpenScheduleOfTheWeek.day.index < currentDayOfWeek;
    const nextScheduleStartDate = DateUtils.dayjsInMarketplaceTimezone()
      .startOf('day')
      .add(
        (isNextWeek ? 7 : 0) + schedulesWithOpenScheduleOfTheWeek.day.index - currentDayOfWeek,
        'day',
      )
      .add(nextOpenedScheduleOfTheWeek.start!, 'minute');
    return `${translations['shop-informations.schedules.open']} ${nextScheduleStartDate.fromNow()} ${translations['shop-informations.schedules.at']} ${nextScheduleStartDate.format(translations['shop-informations.schedules.format'])}`;
  });

  private findNextOpenSchedule(currentDayOfWeek: number) {
    let schedulesWithOpenScheduleOfTheWeek;
    for (let dayIndex = currentDayOfWeek + 1; dayIndex < currentDayOfWeek + 6; dayIndex++) {
      const scheduleForDay = this.groupedSchedules().find(s => s.day.index === dayIndex % 7);
      if (scheduleForDay && scheduleForDay.timeSlots.some(slot => !slot.isClosed)) {
        schedulesWithOpenScheduleOfTheWeek = scheduleForDay;
        break;
      }
    }
    return schedulesWithOpenScheduleOfTheWeek;
  }

  getHours(value: number): string {
    const hours = Math.floor(value / 60);
    return `${DateUtils.dayjsInMarketplaceTimezone().hour(hours).format(this.translateService.instant('shop-informations.schedules.hours-format'))}${this.translateService.instant('shop-informations.schedules.separate-hours-format')}${DateUtils.dayjsInMarketplaceTimezone()
      .minute(value % 60)
      .format(
        'mm',
      )}${this.translateService.instant('shop-informations.schedules.end-format') ? DateUtils.dayjsInMarketplaceTimezone().hour(hours).format(this.translateService.instant('shop-informations.schedules.end-format')) : ''}`;
  }

  toggleAccordion(): void {
    this.isAccordionActive.update(state => !state);
  }

  toggleAccordionCalendarPerturbation(): void {
    this.isCalendarPerturbationAccordionActive.update(state => !state);
  }
}
